import type { TFunction } from "i18next";
import { DateTime, Info } from "luxon";
import type { ConflictItem } from "../../../components/UIToolkit/Popups/ConflictBlockedDialog";
import { formatIsoDateLocale, formatTimeRange } from "../../../utils/dateUtils";
import { getDayIndex } from "../../MasterSchedule/v2/utils";
import type {
	ConflictingOpenShift,
	ConflictingRecurringParams,
	ConflictingRecurringShift,
	ConflictingScheduledShift,
	ConflictingTemplateLocation,
} from "./jobRemovalConflicts";

/**
 * Turns the DAT-805 blocker payload into one-line `ConflictItem`s for the job-removal dialog.
 *
 * Formatting lives here rather than on the backend for two reasons: the backend raises i18n keys and
 * cannot translate, and only the client knows the reader's locale. Timezone needs no handling —
 * `HospitalDataProvider` calls `setDefaultTimezone(chosenDepartment.timezone)`, so `DateTime.fromISO`
 * already lands in department-local time.
 *
 * Labels are single-line and ellipsized by the dialog, so each puts the most identifying fact first:
 * who, then when, then where. Anything that doesn't fit the line goes in the `tag` chip instead.
 */

/** Joined with a middle dot, dropping the segments this row has no value for. */
const joinSegments = (segments: (string | null | undefined)[]): string => segments.filter((segment): segment is string => Boolean(segment?.trim())).join(" · ");

/** "7:00 AM - 7:00 PM" from two ISO datetimes; just the start when there is no end. */
const isoTimeRange = (startIso: string, endIso: string | null): string | null => {
	const start = DateTime.fromISO(startIso);
	if (!start.isValid) return null;
	const end = endIso ? DateTime.fromISO(endIso) : null;
	return end?.isValid ? formatTimeRange(start, end) : start.toFormat("h:mm a");
};

/**
 * "7:00 AM - 7:00 PM" from two clock times ("07:00", "19:00:00"), which is what `recurring_shift` and
 * `shift_template` store — neither has a date to anchor a real datetime to.
 */
const clockTimeRange = (start: string | null, end: string | null): string | null => {
	const parse = (value: string | null) => (value ? DateTime.fromFormat(value.substring(0, 5), "HH:mm") : null);
	const from = parse(start);
	if (!from?.isValid) return null;
	const to = parse(end);
	return to?.isValid ? formatTimeRange(from, to) : from.toFormat("h:mm a");
};

/** Locale-short weekday names in the order given, e.g. ["monday","wednesday"] → "Mon, Wed". */
const formatWeekdays = (weekdays: string[]): string | null => {
	// `Info.weekdays` is Monday-first (index 0 = Monday) while `getDayIndex` returns 0 = Sunday, so the
	// lookup has to shift by one rather than index directly.
	const shortNames = Info.weekdays("short");
	const labels = weekdays
		.map((day) => {
			const sundayFirst = getDayIndex(day);
			return sundayFirst === null ? null : shortNames[(sundayFirst + 6) % 7];
		})
		.filter((label): label is string => Boolean(label));
	return labels.length > 0 ? labels.join(", ") : null;
};

/**
 * How often the shift repeats, in the reader's words. `recurring_params` is a JSONB column written by
 * three different Pydantic subclasses, and 2 prod rows have no `recurring_type` at all, so every field
 * is treated as absent-until-proven.
 */
const formatRecurrence = (params: ConflictingRecurringParams | null, t: TFunction): string | null => {
	if (!params) return null;
	const type = params.recurring_type ?? params.recurringType;

	if (type === "Weekly") {
		const days = formatWeekdays(params.weekdays ?? []);
		if (!days) return null;
		// Biweekly is the dominant prod pattern (interval 2 on ~110k rows), and "Wed" alone would read
		// as every week — a materially different shift from the one she is looking at.
		return params.interval && params.interval > 1 ? t("department.job.remove.conflict.everyOtherDays", { days }) : days;
	}
	if (type === "ConsecutiveDays") {
		const { working_days_amount: working, rest_days_amount: rest } = params;
		return working && rest ? t("department.job.remove.conflict.consecutiveDays", { working, rest }) : null;
	}
	if (type === "Daily") return t("department.job.remove.conflict.daily");
	return null;
};

export const scheduledShiftItem = (shift: ConflictingScheduledShift, t: TFunction): ConflictItem => ({
	id: shift.shiftId,
	label:
		joinSegments([shift.nurseName, formatIsoDateLocale(shift.startTime) || null, isoTimeRange(shift.startTime, shift.endTime), shift.location]) ||
		t("department.job.remove.conflict.unknownItem"),
	tag: shift.shiftType ?? undefined,
});

export const recurringShiftItem = (shift: ConflictingRecurringShift, t: TFunction): ConflictItem => ({
	id: shift.recurringShiftId,
	label:
		joinSegments([shift.nurseName, formatRecurrence(shift.recurringParams, t), clockTimeRange(shift.shiftStart, shift.shiftEnd), shift.location]) ||
		t("department.job.remove.conflict.unknownItem"),
});

export const templateLocationItem = (template: ConflictingTemplateLocation, t: TFunction): ConflictItem => ({
	id: template.templateId,
	// The template's own short name is what the Schedule Template grid labels the slot with, so it is
	// what she will be scanning for; `section` disambiguates the same slot name across units.
	// DEFAULT: fall back to the full `location` when `location_short_name` is empty. The column is NOT
	// NULL but not non-blank, and a slot identified by neither name is unfindable — the full name is
	// the same slot under a longer label, which is strictly better than an anonymous row.
	label:
		joinSegments([template.locationShortName || template.location, template.section, clockTimeRange(template.shiftStart, template.shiftEnd)]) ||
		t("department.job.remove.conflict.unknownItem"),
	tag: template.shiftType || undefined,
});

export const openShiftItem = (request: ConflictingOpenShift, t: TFunction): ConflictItem => ({
	id: request.requestId,
	label:
		joinSegments([
			formatIsoDateLocale(request.shiftStart) || null,
			isoTimeRange(request.shiftStart, request.shiftEnd),
			request.location,
			request.agencySourceName,
		]) || t("department.job.remove.conflict.unknownItem"),
	// DEFAULT: `sub_role` then `shift_type` then no chip. The sub-role is the more specific of the two
	// (an agency request's "LPN" against a plain "Day"), and only agency-sourced requests carry one.
	tag: request.subRole ?? request.shiftType ?? undefined,
});

/**
 * Disclosure label for a category whose item list is a capped sample.
 *
 * Two things it has to say. **What** it lists — five collapsed toggles stack up under the counts, and
 * "Show first 25 of 15,665" alone leaves the reader guessing which category it belongs to. And
 * **whether the list is complete** — a bare "Show 25" against a count of 15,665 reads as a checklist
 * she could work through, which it isn't.
 *
 * `categoryKey` is the plural noun for the category ("scheduled shifts"), lower-case in each locale
 * rather than reusing the tile's title-case label, so the sentence reads as a sentence.
 */
export const conflictToggleLabel = (shown: number, count: number, categoryKey: string, t: TFunction): string => {
	const category = t(categoryKey);
	return shown < count
		? t("department.job.remove.conflict.showFirstOf", { shown, count, category })
		: t("department.job.remove.conflict.showItems", { count, category });
};
